/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Child Detail Page - Guardian view of child's progress and challenges
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import api from '../services/api'
import {
  ArrowLeft,
  Target,
  Clock,
  TrendingUp,
  BookOpen,
  CheckCircle,
  PlayCircle,
  Calendar,
  FileText,
  Eye,
  ChevronRight,
  UserCheck,
  MessageSquare,
  Trash2,
  BarChart3,
  Trophy,
  Network
} from 'lucide-react'
import coachService from '../services/coachService'
import KnowledgeMap from './KnowledgeMap'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { useChildProgress } from '../hooks/useChildProgress'
import {
  PerSubjectScoreDisplay,
  CombinedTrendChart,
  PracticePlanCard,
  PersonalizedRecommendations
} from '../components/scores'
import BadgeDisplay from '../components/badges/BadgeDisplay'
import ActivityHeatmap from '../components/heatmap/ActivityHeatmap'
import StreakTile from '../components/heatmap/StreakTile'
import { createLogger } from '../utils/logger'

const logger = createLogger('ChildDetailPage')

const ChildDetailPage = () => {
  const { childId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [childData, setChildData] = useState(null)
  const [childStats, setChildStats] = useState(null)
  const [challenges, setChallenges] = useState([])
  const [coaches, setCoaches] = useState([])
  const [loading, setLoading] = useState(true)
  const [challengesLoading, setChallengesLoading] = useState(true)
  const [coachesLoading, setCoachesLoading] = useState(true)
  const [removingCoachId, setRemovingCoachId] = useState(null)
  // Which view is shown: the progress overview or the child's read-only knowledge map.
  const [view, setView] = useState('overview')

  // Fetch progress data for the child (SAT scores, recommendations, etc.)
  const { data: progressData, loading: progressLoading } = useChildProgress(childId)

  // Fetch child details and stats
  useEffect(() => {
    const loadChildData = async () => {
      try {
        setLoading(true)
        const response = await api.get(`/family/children/${childId}`)

        if (response.data.success) {
          setChildData(response.data.child)
          setChildStats(response.data.stats)
        }
      } catch (error) {
        logger.error('Error loading child data:', error)
        toast.error('Failed to load child details')
        navigate('/dashboard')
      } finally {
        setLoading(false)
      }
    }

    loadChildData()
  }, [childId, navigate])

  // Fetch child challenges
  useEffect(() => {
    const loadChallenges = async () => {
      try {
        setChallengesLoading(true)
        const response = await api.get(`/family/children/${childId}/challenges`)

        if (response.data.success) {
          setChallenges(response.data.challenges)
        }
      } catch (error) {
        logger.error('Error loading challenges:', error)
        toast.error('Failed to load challenges')
      } finally {
        setChallengesLoading(false)
      }
    }

    if (childId) {
      loadChallenges()
    }
  }, [childId])

  // Fetch child's coaches
  useEffect(() => {
    const loadCoaches = async () => {
      try {
        setCoachesLoading(true)
        const response = await api.get(`/family/children/${childId}/coaches`)

        if (response.data.success) {
          setCoaches(response.data.coaches)
        }
      } catch (error) {
        logger.error('Error loading coaches:', error)
      } finally {
        setCoachesLoading(false)
      }
    }

    if (childId) {
      loadCoaches()
    }
  }, [childId])

  const handleRemoveCoach = async (coachId) => {
    if (!confirm('Are you sure you want to remove this coach?')) {
      return
    }

    try {
      setRemovingCoachId(coachId)
      await api.delete(`/family/children/${childId}/coaches/${coachId}`)
      setCoaches((prev) => prev.filter((c) => c.id !== coachId))
      toast.success('Coach removed successfully')
    } catch (error) {
      logger.error('Error removing coach:', error)
      toast.error('Failed to remove coach')
    } finally {
      setRemovingCoachId(null)
    }
  }

  const formatTime = (seconds) => {
    if (!seconds) return '0m'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  const getStatusBadge = (status) => {
    const badges = {
      completed: {
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-700 dark:text-green-300',
        label: 'Completed'
      },
      active: {
        bg: 'bg-purple-100 dark:bg-purple-900/30',
        text: 'text-purple-700 dark:text-purple-300',
        label: 'In Progress'
      },
      draft: {
        bg: 'bg-gray-100 dark:bg-gray-800',
        text: 'text-gray-600 dark:text-gray-400',
        label: 'Draft'
      }
    }

    const badge = badges[status] || badges.draft

    return (
      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!childData || !childStats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">Child not found</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {childData.firstName} {childData.lastName}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">{childData.email}</p>
        </div>
        {childData.age && (
          <span className="text-sm px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-medium">
            {childData.age} years old
          </span>
        )}
      </div>

      {/* View tabs: progress overview vs. read-only knowledge map */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-1">
          {[
            ['overview', 'Overview', BarChart3],
            ['knowledge-map', 'Knowledge Map', Network]
          ].map(([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => setView(id)}
              aria-pressed={view === id}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                view === id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === 'knowledge-map' ? (
        <KnowledgeMap childId={childId} userName={childData.firstName} embedded />
      ) : (
        <>
          {/* Statistics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Challenges */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Challenges</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {childStats.totalChallenges}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Completed */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Completed</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {childStats.completedChallenges}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* In Progress */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <PlayCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">In Progress</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {childStats.inProgressChallenges}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Average Score */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <Target className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Average Score</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {childStats.averageScore !== null
                        ? `${Math.round(childStats.averageScore * 10) / 10}%`
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-gray-500" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Accuracy Rate
                  </p>
                </div>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {childStats.accuracyRate !== null
                    ? `${Math.round(childStats.accuracyRate * 10) / 10}%`
                    : 'N/A'}
                </p>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Time Spent</p>
                </div>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {formatTime(childStats.totalTimeSpent)}
                </p>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Questions Answered
                  </p>
                </div>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {childStats.totalQuestionsAnswered}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {childStats.totalCorrectAnswers} correct
                </p>
              </div>
            </div>
          </div>

          {/* Activity Heatmap & Streak Section - Right after stats */}
          {!progressLoading && progressData.activityHeatmap && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ActivityHeatmap
                activities={progressData.activityHeatmap.activities}
                stats={progressData.activityHeatmap.stats}
                className="w-full"
              />
              <StreakTile stats={progressData.activityHeatmap.stats} />
            </div>
          )}

          {/* Enhanced Progress Sections */}
          {progressLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">
                Loading detailed progress...
              </span>
            </div>
          ) : (
            <>
              {/* 1. SAT Score Section */}
              {progressData.perSubjectScores && (
                <div className="card">
                  <div className="card-header">
                    <div className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-primary-600" />
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        SAT Scores
                      </h2>
                    </div>
                  </div>
                  <div className="card-body">
                    <PerSubjectScoreDisplay
                      mathData={progressData.perSubjectScores.math}
                      readingWritingData={progressData.perSubjectScores.readingWriting}
                      combinedData={progressData.perSubjectScores.combined}
                      showCurrent={true}
                      showPredicted={true}
                      showTrend={true}
                    />
                  </div>
                </div>
              )}

              {/* 2. Score Trend Section */}
              {(progressData.mathActualHistory || progressData.rwActualHistory) && (
                <div className="card">
                  <div className="card-header">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-primary-600" />
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Score Trend
                      </h2>
                    </div>
                  </div>
                  <div className="card-body">
                    <CombinedTrendChart
                      mathActualHistory={progressData.mathActualHistory?.history || []}
                      rwActualHistory={progressData.rwActualHistory?.history || []}
                      mathPredictedHistory={
                        progressData.perSubjectScores?.math?.trend?.history || []
                      }
                      rwPredictedHistory={
                        progressData.perSubjectScores?.readingWriting?.trend?.history || []
                      }
                      mathUncertainty={progressData.perSubjectScores?.math?.uncertaintyBand}
                      rwUncertainty={progressData.perSubjectScores?.readingWriting?.uncertaintyBand}
                      goalScore={progressData.goal?.goalSatScore}
                      goalDate={progressData.practiceRecommendation?.testDate}
                      height={280}
                      showConfidenceBands={true}
                      showProjections={true}
                    />
                  </div>
                </div>
              )}

              {/* 3. Practice Plan Section (read-only) */}
              {progressData.goal?.goalSatScore && progressData.practiceRecommendation && (
                <PracticePlanCard data={progressData.practiceRecommendation} readOnly={true} />
              )}

              {/* 4. Personalized Recommendations (read-only) */}
              {progressData.goal?.goalSatScore && (
                <PersonalizedRecommendations
                  recommendations={progressData.recommendations}
                  loading={false}
                  readOnly={true}
                />
              )}

              {/* 5. Recent Achievements Section */}
              <div className="card">
                <div className="card-header">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Recent Achievements
                    </h2>
                  </div>
                </div>
                <div className="card-body">
                  <BadgeDisplay userId={childId} />
                </div>
              </div>
            </>
          )}

          {/* Coaches Section */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Active Coaches
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Coaches working with {childData.firstName}
                  </p>
                </div>
              </div>

              {coachesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-gray-600 dark:text-gray-400">Loading coaches...</span>
                </div>
              ) : coaches.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  <UserCheck className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No coaches assigned yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {coaches.map((coach) => (
                    <div
                      key={coach.id}
                      className="flex items-center justify-between gap-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium">
                          {coach.firstName?.charAt(0) || coach.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {coach.firstName && coach.lastName
                              ? `${coach.firstName} ${coach.lastName}`
                              : coach.name || coach.email}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{coach.email}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            navigate(
                              `/messages?conversationType=coaching&childId=${childId}&coachId=${coach.id}`
                            )
                          }
                          className="btn btn-sm btn-secondary inline-flex items-center gap-1.5"
                          title="View messages with this coach"
                        >
                          <MessageSquare className="w-4 h-4" />
                          <span className="hidden sm:inline">Messages</span>
                        </button>
                        <button
                          onClick={() => handleRemoveCoach(coach.id)}
                          disabled={removingCoachId === coach.id}
                          className="btn btn-sm btn-danger inline-flex items-center gap-1.5"
                          title="Remove this coach"
                        >
                          {removingCoachId === coach.id ? (
                            <LoadingSpinner size="xs" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                          <span className="hidden sm:inline">Remove</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Challenges List */}
          <div className="card">
            <div className="card-body">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Challenge History
              </h2>

              {challengesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    Loading challenges...
                  </span>
                </div>
              ) : challenges.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No challenges yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {challenges.map((challenge) => (
                    <div
                      key={challenge.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                              {challenge.name || 'SAT Practice Challenge'}
                            </h3>
                            {getStatusBadge(challenge.status)}
                          </div>

                          {challenge.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                              {challenge.description}
                            </p>
                          )}

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                                Score
                              </p>
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {challenge.totalScore !== null
                                  ? `${Math.round(challenge.totalScore * 10) / 10}%`
                                  : 'N/A'}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                                Questions
                              </p>
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {challenge.correctAnswers || 0}/{challenge.totalQuestions || 0}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                                Time
                              </p>
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {formatTime(challenge.totalTime)}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                                Date
                              </p>
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {new Date(challenge.createdAt).toLocaleDateString(undefined, {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* View Details Button */}
                        {challenge.status === 'completed' && (
                          <button
                            onClick={() => navigate(`/challenge/${challenge.id}/report`)}
                            className="btn btn-sm btn-secondary inline-flex items-center gap-1 whitespace-nowrap"
                          >
                            <Eye className="w-4 h-4" />
                            <span className="hidden sm:inline">View</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ChildDetailPage
